import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

class AuthService {
  constructor() {
    this.baseURL = process.env.REACT_APP_WORKER_URL || 'http://localhost:8787';
    this.isDevelopmentMode = process.env.REACT_APP_DEVELOPMENT_MODE === 'true' || process.env.NODE_ENV === 'development';
    this.currentUser = this.loadUserFromStorage();
    
    this.client = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 5000, // 5 second timeout
    });

    // Add auth interceptor
    this.client.interceptors.request.use((config) => {
      const token = this.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  // User session management
  loadUserFromStorage() {
    try {
      const userData = localStorage.getItem('cloudstream_user');
      return userData ? JSON.parse(userData) : null;
    } catch {
      return null;
    }
  }

  saveUserToStorage(user) {
    localStorage.setItem('cloudstream_user', JSON.stringify(user));
    this.currentUser = user;
  }

  clearUserFromStorage() {
    localStorage.removeItem('cloudstream_user');
    localStorage.removeItem('cloudstream_token');
    this.currentUser = null;
  }

  getToken() {
    return localStorage.getItem('cloudstream_token');
  }

  setToken(token) {
    localStorage.setItem('cloudstream_token', token);
  }

  // Authentication methods
  async login(credentials) {
    try {
      const response = await this.client.post('/api/auth/login', credentials);
      const { user, token } = response.data;
      
      this.setToken(token);
      this.saveUserToStorage(user);
      
      return { user, token };
    } catch (error) {
      console.error('Login error:', error);
      
      // In development mode, provide mock authentication if backend is unavailable
      if (this.isDevelopmentMode && (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK')) {
        console.warn('Backend unavailable, using mock authentication for development');
        return this.mockLogin(credentials);
      }
      
      throw error;
    }
  }

  mockLogin(credentials) {
    const mockUser = {
      id: 'dev-user-' + Date.now(),
      email: credentials.email || 'demo@cloudstream.dev',
      displayName: credentials.displayName || 'Demo User',
      role: 'admin',
      isGuest: false,
      avatar: null,
      settings: {
        theme: 'dark',
        notifications: true,
        emailUpdates: true,
        language: 'en',
        timezone: 'UTC',
      },
      permissions: {
        createStreams: true,
        manageInvitations: true,
        manageUsers: true,
        viewAnalytics: true,
      },
      createdAt: new Date().toISOString(),
    };

    const mockToken = 'mock-jwt-token-' + Date.now();
    
    this.setToken(mockToken);
    this.saveUserToStorage(mockUser);
    this.currentUser = mockUser;
    
    return { user: mockUser, token: mockToken };
  }

  async register(userData) {
    try {
      const response = await this.client.post('/api/auth/register', userData);
      const { user, token } = response.data;
      
      this.setToken(token);
      this.saveUserToStorage(user);
      
      return { user, token };
    } catch (error) {
      console.error('Registration error:', error);
      
      // In development mode, provide mock registration if backend is unavailable
      if (this.isDevelopmentMode && (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK')) {
        console.warn('Backend unavailable, using mock registration for development');
        return this.mockLogin(userData);
      }
      
      throw error;
    }
  }

  async loginAsGuest(displayName = null) {
    try {
      const guestData = {
        displayName: displayName || `Guest-${Date.now()}`,
        isGuest: true,
      };
      
      const response = await this.client.post('/api/auth/guest', guestData);
      const { user, token } = response.data;
      
      this.setToken(token);
      this.saveUserToStorage(user);
      
      return { user, token };
    } catch (error) {
      console.error('Guest login error:', error);
      
      // In development mode, provide mock guest login if backend is unavailable
      if (this.isDevelopmentMode && (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK')) {
        console.warn('Backend unavailable, using mock guest login for development');
        return this.mockGuestLogin(displayName);
      }
      
      throw error;
    }
  }

  mockGuestLogin(displayName) {
    const mockGuest = {
      id: 'guest-user-' + Date.now(),
      displayName: displayName || `Guest User ${Math.floor(Math.random() * 1000)}`,
      role: 'guest',
      isGuest: true,
      avatar: null,
      settings: {
        theme: 'dark',
        notifications: false,
        emailUpdates: false,
        language: 'en',
        timezone: 'UTC',
      },
      permissions: {
        createStreams: false,
        manageInvitations: false,
        manageUsers: false,
        viewAnalytics: false,
      },
      createdAt: new Date().toISOString(),
    };

    const mockToken = 'mock-guest-token-' + Date.now();
    
    this.setToken(mockToken);
    this.saveUserToStorage(mockGuest);
    this.currentUser = mockGuest;
    
    return { user: mockGuest, token: mockToken };
  }

  async logout() {
    try {
      if (!this.currentUser?.isGuest) {
        await this.client.post('/api/auth/logout');
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.clearUserFromStorage();
    }
  }

  async refreshToken() {
    try {
      const response = await this.client.post('/api/auth/refresh');
      const { token } = response.data;
      this.setToken(token);
      return token;
    } catch (error) {
      console.error('Token refresh error:', error);
      this.logout();
      throw error;
    }
  }

  // User profile management
  async updateProfile(updates) {
    try {
      const response = await this.client.put('/api/auth/profile', updates);
      const updatedUser = response.data;
      
      this.saveUserToStorage(updatedUser);
      return updatedUser;
    } catch (error) {
      console.error('Profile update error:', error);
      throw error;
    }
  }

  async changePassword(currentPassword, newPassword) {
    try {
      await this.client.put('/api/auth/password', {
        currentPassword,
        newPassword,
      });
      return true;
    } catch (error) {
      console.error('Password change error:', error);
      throw error;
    }
  }

  async uploadAvatar(file) {
    try {
      const formData = new FormData();
      formData.append('avatar', file);

      const response = await this.client.post('/api/auth/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const updatedUser = response.data;
      this.saveUserToStorage(updatedUser);
      return updatedUser;
    } catch (error) {
      console.error('Avatar upload error:', error);
      throw error;
    }
  }

  // Invitation system
  async createInvitation(invitationData) {
    try {
      const response = await this.client.post('/api/invitations', {
        ...invitationData,
        createdBy: this.currentUser?.id,
      });
      return response.data;
    } catch (error) {
      console.error('Create invitation error:', error);
      throw error;
    }
  }

  async getInvitations() {
    try {
      const response = await this.client.get('/api/invitations');
      return response.data;
    } catch (error) {
      console.error('Get invitations error:', error);
      return [];
    }
  }

  async getInvitation(inviteCode) {
    try {
      const response = await this.client.get(`/api/invitations/${inviteCode}`);
      return response.data;
    } catch (error) {
      console.error('Get invitation error:', error);
      throw error;
    }
  }

  async acceptInvitation(inviteCode, userData = {}) {
    try {
      const response = await this.client.post(`/api/invitations/${inviteCode}/accept`, userData);
      const { user, token } = response.data;
      
      if (token) {
        this.setToken(token);
        this.saveUserToStorage(user);
      }
      
      return response.data;
    } catch (error) {
      console.error('Accept invitation error:', error);
      throw error;
    }
  }

  async revokeInvitation(inviteCode) {
    try {
      await this.client.delete(`/api/invitations/${inviteCode}`);
      return true;
    } catch (error) {
      console.error('Revoke invitation error:', error);
      throw error;
    }
  }

  // Organization/Team management
  async createOrganization(orgData) {
    try {
      const response = await this.client.post('/api/organizations', orgData);
      return response.data;
    } catch (error) {
      console.error('Create organization error:', error);
      throw error;
    }
  }

  async getOrganizations() {
    try {
      const response = await this.client.get('/api/organizations');
      return response.data;
    } catch (error) {
      console.error('Get organizations error:', error);
      return [];
    }
  }

  async joinOrganization(orgId, inviteCode = null) {
    try {
      const response = await this.client.post(`/api/organizations/${orgId}/join`, {
        inviteCode,
      });
      return response.data;
    } catch (error) {
      console.error('Join organization error:', error);
      throw error;
    }
  }

  // Utility methods
  isAuthenticated() {
    return !!this.currentUser && !!this.getToken();
  }

  isGuest() {
    return this.currentUser?.isGuest === true;
  }

  getCurrentUser() {
    return this.currentUser;
  }

  hasRole(role) {
    if (!this.currentUser) return false;
    if (Array.isArray(role)) {
      return role.includes(this.currentUser.role);
    }
    return this.currentUser.role === role;
  }

  canCreateStreams() {
    return this.hasRole(['host', 'admin', 'premium']);
  }

  canManageInvitations() {
    return this.hasRole(['admin', 'moderator']);
  }

  generateInviteUrl(inviteCode, streamId = null) {
    const baseUrl = window.location.origin;
    const params = new URLSearchParams();
    
    if (streamId) {
      params.append('stream', streamId);
    }
    
    const queryString = params.toString();
    return `${baseUrl}/invite/${inviteCode}${queryString ? `?${queryString}` : ''}`;
  }

  // Mock data for development
  getMockUser() {
    return {
      id: 'user-123',
      email: 'demo@example.com',
      displayName: 'Demo User',
      role: 'host',
      avatar: 'https://via.placeholder.com/100x100/0051c3/ffffff?text=DU',
      isGuest: false,
      createdAt: new Date().toISOString(),
      organizations: ['org-1'],
      settings: {
        notifications: true,
        emailUpdates: true,
        theme: 'dark',
      },
    };
  }
}

export const authService = new AuthService(); 